/**
 * Login — "Site Office" magic-link entry point.
 *
 * Visual design (redesign/rc-screens): two-pane `.login-grid` — left pane is
 * a kinetic vertical-marquee hero (paired `.vmarq` columns scrolling in
 * opposite directions, outlined display type), right pane is the magic-link
 * form using the shared `.inx` input + `.btnx.primary` button primitives.
 * Day/night toggle reuses the existing <ThemeToggle /> (do not reinvent).
 *
 * LOGIC UNCHANGED — only the rendering layer is restyled. Same apiFetch
 * call, same idle/sending/sent/error state machine as before.
 */
import { useState } from 'react';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { apiFetch } from '../lib/api.js';
import ThemeToggle from '../components/ThemeToggle.jsx';

// Decorative only — tier/process vocabulary, doubled so the vertical
// scroll loops seamlessly (each .vcol translates itself by -50%).
const MARQUEE_WORDS = [
  'FOUNDATION', 'LEAD RECOVERY', 'ALWAYS OPEN', 'ONBOARDING',
  'BUILD', 'LAUNCH', 'DELIVERED', 'SUPPORT',
];

function MarqueeColumn({ words, reverse }) {
  const doubled = [...words, ...words];
  return (
    <div className={`vcol${reverse ? ' rev' : ''}`}>
      {doubled.map((w, i) => (
        <span
          key={`${w}-${i}`}
          className="d-xl outline-t"
          style={{ fontSize: '11vw', padding: '0.18em 0', whiteSpace: 'nowrap' }}
        >
          {w}
        </span>
      ))}
    </div>
  );
}

export default function Login() {
  const [email, setEmail] = useState('');
  const [state, setState] = useState({ status: 'idle', message: '' });

  async function onSubmit(e) {
    e.preventDefault();
    if (!email) return;
    setState({ status: 'sending', message: '' });
    try {
      await apiFetch('/api/console/auth/request', { method: 'POST', body: { email } });
      setState({
        status:  'sent',
        message: `Check ${email} for a sign-in link. It expires in 10 minutes.`,
      });
    } catch (err) {
      setState({ status: 'error', message: err.message || 'Could not send the link.' });
    }
  }

  const isSending = state.status === 'sending';
  const isSent    = state.status === 'sent';

  return (
    <div className="gridbg login-grid">
      {/* ── Left: kinetic marquee hero ────────────────────────────────────── */}
      <div
        className="panel"
        style={{
          position: 'relative',
          overflow: 'hidden',
          borderRight: '1px solid var(--line)',
          minHeight: '100dvh',
        }}
      >
        <div className="vmarq" aria-hidden="true">
          <MarqueeColumn words={MARQUEE_WORDS} />
          <MarqueeColumn words={[...MARQUEE_WORDS].reverse()} reverse />
        </div>

        <div
          className="crosshair"
          style={{
            position: 'relative',
            zIndex: 1,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            padding: '40px',
          }}
        >
          <span className="stamp stamp-in" style={{ color: 'var(--acc-ink)', alignSelf: 'flex-start' }}>
            Client Portal
          </span>
          <h1 className="d-xl" style={{ fontSize: '52px', margin: '20px 0 0', color: 'var(--ink)' }}>
            TITAN
            <br />
            <span className="serif-i" style={{ textTransform: 'none', color: 'var(--acc-ink)' }}>
              Pipelines
            </span>
          </h1>
          <p className="mono" style={{ marginTop: '14px', color: 'var(--ink-soft)', fontSize: '12px' }}>
            Your project, tracked end to end.
          </p>
        </div>
      </div>

      {/* ── Right: magic-link form ───────────────────────────────────────── */}
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100dvh' }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '12px 16px' }}>
          <ThemeToggle />
        </div>

        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px 20px 64px',
          }}
        >
          <div style={{ width: '100%', maxWidth: '380px' }}>
            <span className="mlabel">Sign in</span>
            <h2 className="d-xl" style={{ fontSize: '28px', margin: '8px 0 6px', color: 'var(--ink)' }}>
              Your project portal
            </h2>
            <p className="mono" style={{ margin: '0 0 28px', color: 'var(--ink-soft)', fontSize: '12px' }}>
              Magic link — no password to remember.
            </p>

            {!isSent ? (
              <form onSubmit={onSubmit} noValidate style={{ display: 'flex', flexDirection: 'column', gap: '22px' }}>
                <label htmlFor="login-email" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <span className="mlabel">Email address</span>
                  <input
                    id="login-email"
                    type="email"
                    required
                    autoFocus
                    autoComplete="email"
                    value={email}
                    onChange={(e) => { setState((s) => ({ ...s, message: '' })); setEmail(e.target.value); }}
                    placeholder="you@yourbusiness.com"
                    className="inx"
                  />
                </label>

                {state.status === 'error' && state.message && (
                  <p role="alert" className="mono" style={{ margin: 0, fontSize: '12px', color: 'var(--bad)', lineHeight: 1.5 }}>
                    {state.message}
                  </p>
                )}

                <button type="submit" disabled={isSending || !email} className="btnx primary" style={{ width: '100%' }}>
                  <span>{isSending ? 'Sending…' : 'Send sign-in link'}</span>
                  {!isSending && <ArrowRight size={16} className="arr" aria-hidden="true" />}
                </button>
              </form>
            ) : (
              /* ── Sent state ──────────────────────────────────────────────── */
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '14px' }}>
                <span className="stamp stamp-in" style={{ color: 'var(--ok)' }}>
                  <CheckCircle2 size={13} aria-hidden="true" />
                  Link sent
                </span>
                <div>
                  <p className="d-xl" style={{ fontSize: '18px', margin: '0 0 6px', color: 'var(--ink)' }}>
                    Check your email
                  </p>
                  <p style={{ margin: 0, color: 'var(--ink-soft)', fontSize: '14px', lineHeight: 1.5 }}>
                    {state.message}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => { setEmail(''); setState({ status: 'idle', message: '' }); }}
                  className="btnx quiet"
                  style={{ height: '40px', fontSize: '11px' }}
                >
                  Use a different email
                </button>
              </div>
            )}

            <p className="mono" style={{ marginTop: '40px', fontSize: '11px', color: 'var(--ink-soft)' }}>
              Titan Pipelines — your account manager
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
